import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Zap, Check, Info, ChevronRight, Eye, EyeOff } from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { cn } from "@shared/lib/utils";
import {
  useGetChoices,
  useSetupConnection,
  useUpdateConnectionSetup,
  useGetConnectionDetails,
} from "../hooks";
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";

interface ConnectionStepProps {
  integrationKey: "aclarian" | "paymentus" | "neptune";
  title: string;
  defaultIntegrationName: string;
  defaultApiBaseUrl: string;
  defaultAuthType: string | number;
  defaultApiKey: string;
  envInfoText: string;
  testPingUrl: string;
  authTypeChoices?: { label: string; value: string | number }[];
  // standard stepper props
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

interface ConnectionFormData {
  integrationName: string;
  authType: string;
  apiBaseUrl: string;
  apiKey?: string;
  clientId?: string;
  clientSecret?: string;
  tokenUrl?: string;
  username?: string;
  password?: string;
}

export const ConnectionStep: React.FC<ConnectionStepProps> = ({
  integrationKey,
  title,
  defaultIntegrationName,
  defaultApiBaseUrl,
  defaultAuthType,
  defaultApiKey,
  envInfoText,
  testPingUrl,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const navigate = useNavigate();
  const platformUrl = import.meta.env.VITE_PLATFORM_URL;
  const urlParts = platformUrl?.split("//")[1]?.split(".") || [];
  const rawEnvironment = urlParts[0]?.replace("platform-", "") || "production";
  const formattedEnvironment =
    rawEnvironment.charAt(0).toUpperCase() + rawEnvironment.slice(1);
  const dynamicIntegrationName = `${title} — ${formattedEnvironment}`;
  const { action, id } = useParams<{ action: string; id: string }>();
  const setupConnection = useSetupConnection();
  const updateConnectionSetup = useUpdateConnectionSetup(id || "");
  const tenantId = useTenantId();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: authTypeData } = useGetChoices("auth_type");

  const isPending =
    action === "configure" || !!id
      ? updateConnectionSetup.isPending
      : setupConnection.isPending;

  const { data: connectionDetails } = useGetConnectionDetails(
    id,
    action === "configure",
  );
  const authTypeChoices = useMemo(
    () =>
      authTypeData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [authTypeData],
  );

  // Connection testing state
  const [testState, setTestState] = useState<
    "idle" | "testing" | "success" | "error"
  >("idle");

  // Separate refs for each data source to avoid race condition:
  // apiDataLoadedRef  — tracks whether we've populated from the API response
  // localDataLoadedRef — tracks whether we've populated from local storage
  const apiDataLoadedRef = useRef(false);
  const localDataLoadedRef = useRef(false);

  // Define form fields using FormService pattern
  const connectionFields: FormField[] = useMemo(
    () => [
      {
        name: "integrationName",
        label: "Integration name",
        type: "text",
        required: true,
        placeholder: `Enter a friendly name for ${title}`,
        helperText:
          "A friendly name shown across SMART360 to identify this connection.",
        showHelperTooltip: true,
        className: "col-span-2",
        defaultValue: connectionDetails?.name || dynamicIntegrationName,
      },

      {
        name: "authType",
        label: "Authentication type",
        type: "select",
        required: true,
        placeholder: "Select auth method",
        options: authTypeChoices,
        className: "col-span-2",
      },
      {
        name: "apiKey",
        label: "API Key / Token",
        type: "password",
        required: true,
        disableAutofill: true,
        placeholder: "Enter your secret token",
        helperText:
          "Encrypted with AES-256 - only the integration runtime can decrypt.",
        className: "col-span-2",
        showWhen: (formValues) => Number(formValues.authType) === 1,
      },
      {
        name: "clientId",
        label: "Client ID",
        type: "text",
        required: true,
        disableAutofill: true,
        placeholder: "Enter your client ID",
        showWhen: (formValues) => Number(formValues.authType) === 0,
      },
      {
        name: "clientSecret",
        label: "Secret Key",
        type: "password",
        required: true,
        disableAutofill: true,
        placeholder: "Enter your client secret",
        showWhen: (formValues) => Number(formValues.authType) === 0,
      },
      {
        name: "tokenUrl",
        label: "Token URL",
        type: "text",
        required: true,
        autoComplete: "off",
        placeholder: "https://auth.aclarian.com/oauth2/token",
        showWhen: (formValues) => Number(formValues.authType) === 0,
        className: "col-span-2",
      },
      {
        name: "username",
        label: "Username",
        type: "text",
        required: true,
        disableAutofill: true,
        placeholder: "Enter your basic auth username",
        showWhen: (formValues) => Number(formValues.authType) === 3,
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        required: true,
        disableAutofill: true,
        placeholder: "Enter your basic auth password",
        showWhen: (formValues) => Number(formValues.authType) === 3,
      },
    ],
    [authTypeChoices, title, defaultIntegrationName, connectionDetails],
  );

  // Create form instance using FormService
  const connectionForm = FormService.createForm<ConnectionFormData>({
    fields: connectionFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      integrationName: connectionDetails?.name || dynamicIntegrationName,
      authType: "",
      apiBaseUrl: "",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      tokenUrl: "",
      username: "",
      password: "",
    },
  });

  // Helper to save step data
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save connection step data:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      const currentFormData = connectionForm.getValues();
      let currentTestState = testState;

      if (fieldName === "authType") {
        currentTestState = "idle";
        setTestState("idle");
      }

      const updatedData = {
        ...currentFormData,
        [fieldName]: value,
        testState: currentTestState,
      };
      saveStepData(updatedData);
    },
    [saveStepData, testState, connectionForm],
  );

  const handleTestConnection = async () => {
    const values = connectionForm.getValues();
    const { apiBaseUrl, authType } = values;

    if (!apiBaseUrl) {
      toast({
        title: "Missing API Base URL",
        description: "Please enter a valid API Base URL to test.",
        variant: "destructive",
      });
      return;
    }

    const authTypeNum =
      authType !== "" && authType !== null && authType !== undefined
        ? Number(authType)
        : -1;
    const isApiKey = authTypeNum === 1;
    const isOAuth = authTypeNum === 0;
    const isBasic = authTypeNum === 3;

    if (isApiKey && !values.apiKey) {
      toast({
        title: "Missing API Key/Token",
        description: "Please enter your API Key/Token to test.",
        variant: "destructive",
      });
      return;
    }

    if (
      isOAuth &&
      (!values.clientId || !values.clientSecret || !values.tokenUrl)
    ) {
      toast({
        title: "Missing OAuth 2.0 Fields",
        description: "Please populate all OAuth 2.0 fields to test connection.",
        variant: "destructive",
      });
      return;
    }

    if (isBasic && (!values.username || !values.password)) {
      toast({
        title: "Missing Basic Auth Fields",
        description:
          "Please populate username and password to test connection.",
        variant: "destructive",
      });
      return;
    }

    setTestState("testing");
    saveStepData({
      ...values,
      testState: "testing",
    });

    // Simulate connection ping
    setTimeout(() => {
      setTestState("success");
      saveStepData({
        ...values,
        testState: "success",
      });
      toast({
        title: "Connection Succeeded",
        description: `Successfully verified connection to ${title}.`,
      });
    }, 1500);
  };

  const isVerified = testState === "success";

  // Solution: use two independent refs so each branch tracks its own state.
  useEffect(() => {
    const isEditMode = action === "configure" || !!id;

    // 1. Populate from API response when editing an existing connection.
    //    We only run this once (apiDataLoadedRef) and return early so the
    //    local-storage branch never overwrites fresh API data.
    if (connectionDetails && isEditMode && !apiDataLoadedRef.current) {
      const credential = connectionDetails.credential || {};
      const credentialFields =
        credential.credential_fields || credential.credentialFields || {};

      const populatedData = {
        integrationName: connectionDetails.name,
        authType:
          credential.auth_type !== undefined
            ? String(credential.auth_type)
            : credential.authType !== undefined
              ? String(credential.authType)
              : "",
        apiBaseUrl:
          connectionDetails.base_url || connectionDetails.baseUrl || "",
        apiKey: credentialFields.api_key || credentialFields.apiKey || "",
        clientId: credentialFields.client_id || credentialFields.clientId || "",
        clientSecret:
          credentialFields.client_secret || credentialFields.clientSecret || "",
        tokenUrl: credentialFields.token_url || credentialFields.tokenUrl || "",
        username: credentialFields.username || "",
        password: credentialFields.password || "",
      };

      FormService.populateForm(connectionForm, populatedData, connectionFields);
      apiDataLoadedRef.current = true;
      return; // exit early — never fall through to local storage in edit mode
    }

    // 2. Populate from local storage only for new connections (not edit mode)
    //    and only once.
    if (!isEditMode && stepHelpers && !localDataLoadedRef.current) {
      try {
        const savedData = stepHelpers.getStepData(currentStepIndex);
        if (savedData) {
          FormService.populateForm(connectionForm, savedData, connectionFields);
          if (savedData.testState) {
            setTestState(savedData.testState);
          }
        }
      } catch (error) {
        console.error("Failed to load connection step data:", error);
      } finally {
        localDataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    connectionForm,
    connectionFields,
    connectionDetails,
    action,
    id,
  ]);

  const handleFormSubmit = useCallback(
    (data: ConnectionFormData) => {
      if (clearValidationError) {
        clearValidationError();
      }

      if (!data.integrationName?.trim()) {
        if (setValidationError) {
          setValidationError("Integration name is required.");
        }
        return;
      }

      const isApiKey = Number(data.authType) === 1;
      const isOAuth = Number(data.authType) === 0;
      const isBasic = Number(data.authType) === 3;

      if (isApiKey && !data.apiKey?.trim()) {
        if (setValidationError) {
          setValidationError("API Key/Token is required.");
        }
        return;
      }

      if (isOAuth) {
        if (
          !data.clientId?.trim() ||
          !data.clientSecret?.trim() ||
          !data.tokenUrl?.trim()
        ) {
          if (setValidationError) {
            setValidationError("All OAuth 2.0 fields are required.");
          }
          return;
        }
      }

      if (isBasic && (!data.username?.trim() || !data.password?.trim())) {
        if (setValidationError) {
          setValidationError("Username and password are required.");
        }
        return;
      }

      let credentialPayload: any = {};

      switch (Number(data.authType)) {
        case 1: // API Key
          credentialPayload = {
            api_key: data.apiKey,
          };
          break;
        case 0: // OAuth 2.0
          credentialPayload = {
            client_id: data.clientId,
            client_secret: data.clientSecret,
            token_url: data.tokenUrl,
          };
          break;
        case 3: // Basic Auth
          credentialPayload = {
            username: data.username,
            password: data.password,
          };
          break;
        default:
          break;
      }

      const operation = action === "configure" || !!id ? "CONFIGURE" : "ADD";

      const submitPayload = {
        connector_key:
          integrationKey === "aclarian"
            ? "aclarian_gl"
            : `${integrationKey}_gl`,
        name: data.integrationName,
        remote_tenant_id: Number(tenantId),
        remote_utility_id: Number(remoteUtilityId),
        environment: rawEnvironment === "production" ? 0 : 1, // 1: sandbox, 0: production
        base_url: data.apiBaseUrl,
        auth_type: Number(data.authType),
        credential: credentialPayload,
      };

      const mutationCallbacks = {
        onSuccess: (res: any) => {
          const connectionId = res?.id || res?.result?.id || id;
          const step0Data = {
            ...data,
            connectionId,
            testState,
            isStepComplete: true,
          };

          // Save final connection data
          saveStepData(step0Data);

          if (connectionId) {
            try {
              const storageKey = `${integrationKey}-integration-progress`;
              const restoredRaw = localStorage.getItem(storageKey);
              const progressData = restoredRaw
                ? JSON.parse(restoredRaw)
                : {
                    currentStep: 0,
                    completedSteps: [],
                    skippedSteps: [],
                    stepData: {},
                  };

              progressData.currentStep = 1;
              if (!progressData.completedSteps.includes(0)) {
                progressData.completedSteps.push(0);
              }
              progressData.stepData[0] = {
                ...step0Data,
                timestamp: new Date().toISOString(),
                stepIndex: 0,
              };
              progressData.timestamp = Date.now();
              progressData.version = "1.0.0";

              localStorage.setItem(storageKey, JSON.stringify(progressData));
            } catch (e) {
              console.error("Failed to manually save connection progress:", e);
            }

            if (onNext) {
              onNext();
            }
            navigate(
              `/erp-integration/${integrationKey}/configure/${connectionId}#sync-flows`,
            );
          } else if (onNext) {
            onNext();
          }
        },
        onError: (error: any) => {
          if (setValidationError) {
            setValidationError(error.message || "Failed to setup connection.");
          }
        },
      };

      switch (operation) {
        case "ADD":
          setupConnection.mutate(submitPayload, mutationCallbacks);
          break;
        case "CONFIGURE":
          updateConnectionSetup.mutate(submitPayload, mutationCallbacks);
          break;
        default:
          break;
      }
    },
    [
      saveStepData,
      setValidationError,
      clearValidationError,
      onNext,
      testState,
      integrationKey,
      setupConnection,
      updateConnectionSetup,
      tenantId,
      remoteUtilityId,
      navigate,
      action,
      id,
    ],
  );

  // Custom render for the form groups with styled sections
  const renderCustomFooter = useCallback(
    () => (
      <div className="space-y-6 col-span-2">
        {/* Test connection block */}
        <div className="border-t border-gray-200 pt-6 mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h3 className="text-base font-bold text-gray-900">
                Test connection
              </h3>
              <p className="text-sm text-gray-500 font-normal max-w-2xl">
                Send a lightweight ping to {testPingUrl} to confirm credentials
                work.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testState === "testing"}
            >
              {testState === "testing" ? (
                <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Zap className="w-4 h-4 text-gray-700" />
              )}
              {testState === "testing" ? "Testing..." : "Test Connection"}
            </Button>
          </div>

          {/* Verification successful banner */}
          {isVerified && (
            <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-4 flex gap-3 items-start animate-in fade-in slide-in-from-bottom-2 duration-200">
              <Check className="w-5 h-5 text-[#16a34a] shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="text-sm font-bold text-[#15803d] block">
                  Connection successful
                </span>
                <span className="text-[13px] text-gray-600 font-normal block leading-relaxed">
                  {title} responded in 218ms · API version{" "}
                  {integrationKey === "aclarian" ? "v2.4.1" : "v1.0.0"} ·
                  Tenant: utility-bynry-prod
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-end pt-6 border-t mt-8">
          <Button
            onClick={() => handleFormSubmit(connectionForm.getValues())}
            disabled={isValidating || isPending}
            variant="default"
          >
            {isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    ),
    [
      handleFormSubmit,
      connectionForm,
      isValidating,
      testPingUrl,
      handleTestConnection,
      testState,
      isVerified,
      title,
      integrationKey,
      isPending,
    ],
  );

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <PageHeader
          title="Connection Setup"
          subtitle={`Provide credentials so SMART360 can talk to ${title}. Test the connection before continuing.`}
        />

        {/* Top Right Status Pill */}
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
            isVerified
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-gray-100 text-gray-600 border-gray-200"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${isVerified ? "bg-emerald-500" : "bg-gray-400"}`}
          />
          {isVerified ? "Connection verified" : "Not yet tested"}
        </span>
      </div>

      {/* Environment Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50/60 border border-blue-100 rounded-2xl">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <h4 className="text-sm font-bold text-blue-900 font-semibold leading-normal">
            {formattedEnvironment} environment
          </h4>
          <p className="text-xs text-blue-700 font-semibold leading-relaxed">
            You are currently in the {formattedEnvironment} environment. Ensure
            you use the correct credentials for this environment.
          </p>
        </div>
      </div>

      {/* Dynamic Form */}
      <DynamicForm
        fields={connectionFields}
        form={connectionForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          showProgress: false,
          classes: FIELD_CLASSES,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};

export default ConnectionStep;
